<form [formGroup]="parametersForm" (ngSubmit)="submitForm(true)" class="flex flex-col gap-3 pt-3">

    @if (isSysAdmin() && reportOptions().showSubscriberSelect) {
        @if (subscriber(); as sub) {
            <div class="flex items-center gap-2 text-sm">
                <span class="material-symbols-outlined text-base text-color-secondary">business</span>
                <span class="font-medium">{{ sub.subscriberName }}</span>
            </div>
        } @else {
            <div class="flex items-center gap-2 rounded border border-orange-400 bg-orange-50 px-3 py-2 text-sm text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                <span class="material-symbols-outlined text-base">warning</span>
                <span>Select a subscriber in the Admin tab to run this report.</span>
            </div>
        }
    }

    @for(param of parameterDefinitions(); track param.key) {
        @if(canUseParameter(param)) {

            <div class="flex flex-col gap-1">
                @if(param.renderLabel ?? true) {
                    <label>
                        {{param.label}}
                        @if (param.required) {
                            <span class="text-error"> *</span>
                        }
                    </label>
                
                }
            
                @switch (param.type) {
                    @case ('subscriberSelect') {
                        <ng-container [ngTemplateOutlet]="subscriberSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('checkbox') {
                        <ng-container [ngTemplateOutlet]="checkbox" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('customerAverageAnnualDeliveredUnits') {
                        <ng-container [ngTemplateOutlet]="customerAverageAnnualDeliveredUnits" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('datePeriod') {
                        <ng-container [ngTemplateOutlet]="datePeriod" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('datePicker') {
                        <ng-container [ngTemplateOutlet]="datepicker" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('deliveryTypeMultiSelect') {
                        <ng-container [ngTemplateOutlet]="deliveryTypeMultiSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('inventoryTransactionTypeMultiSelect') {
                        <ng-container [ngTemplateOutlet]="inventoryTransactionTypeMultiSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('hierarchyTreeSelect') {
                        <ng-container [ngTemplateOutlet]="hierarchyTreeSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('productCategoryCostSelect') {
                        <ng-container [ngTemplateOutlet]="productCategoryCostSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('productCategoryReportMultiSelect') {
                        <ng-container [ngTemplateOutlet]="productCategoryReportMultiSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('radioButton') {
                        <ng-container [ngTemplateOutlet]="radioButton" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('select') {
                        <ng-container [ngTemplateOutlet]="select" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('multiSelect') {
                        <ng-container [ngTemplateOutlet]="multiSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }

                    @case ('tradeClassMultiSelect') {
                        <ng-container [ngTemplateOutlet]="tradeClassMultiSelect" [ngTemplateOutletContext]="{param}"></ng-container>
                    }
                }
            </div>
        }
    }

    <div class="flex pt-2 gap-2">
        <p-button severity="secondary" [outlined]="true" icon="pi pi-refresh" label="Reset" class="flex-1" (onClick)="resetForm()" [disabled]="areAnyParametersLoading()" fluid></p-button>
        @if(isReportRunning()){
            <p-button severity="secondary" [outlined]="true" icon="pi pi-times" label="Cancel" class="flex-1" (onClick)="cancelExecution()" fluid></p-button>
        }
        @else {
            <p-button
                type="submit"
                icon="pi pi-play"
                class="flex-1"
                [label]="areAnyParametersLoading() ? 'Loading...' : 'Run Report'"
                [loading]="areAnyParametersLoading()"
                [disabled]="isSubscriberMissing() || (parametersForm.touched && parametersForm.invalid)"
                fluid
            >
            </p-button>
        }
    </div>

    @if (showApiUrlButton()) {
        <div class="flex pt-1">
            <p-button
                severity="secondary"
                [outlined]="true"
                icon="pi pi-link"
                label="Get API URL"
                class="flex-1"
                [loading]="apiUrlButtonLoading()"
                [disabled]="isSubscriberMissing() || areAnyParametersLoading() || (parametersForm.touched && parametersForm.invalid)"
                (onClick)="openApiUrlDialog()"
                fluid
            />
        </div>
    }

    <app-report-api-url-dialog
        [url]="integrationUrl()"
        [(visible)]="isApiUrlDialogVisible"
    />
    
    <ng-template #subscriberSelect let-param="param">
        <app-subscriber-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #checkbox let-param="param">
        <app-checkbox-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #customerAverageAnnualDeliveredUnits  let-param="param">
        <app-customer-average-annual-delivered-units-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #datePeriod  let-param="param">
        <app-date-period-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #datepicker  let-param="param">
        <app-date-picker-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #deliveryTypeMultiSelect let-param="param">
        <app-delivery-type-multi-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #inventoryTransactionTypeMultiSelect let-param="param">
        <app-inventory-transaction-type-multi-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #hierarchyTreeSelect let-param="param">
        <app-hierarchy-tree-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #productCategoryCostSelect let-param="param">
        <app-product-category-cost-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #productCategoryReportMultiSelect let-param="param">
        <app-product-category-report-multi-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #radioButton let-param="param">
        <app-radio-button-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #select let-param="param">
        <app-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #multiSelect let-param="param">
        <app-multi-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

    <ng-template #tradeClassMultiSelect let-param="param">
        <app-trade-class-multi-select-parameter [formControlName]="param.key" [parameterDefinition]="param"/>
    </ng-template>

</form>
